import { useRoleAuth } from '@src/hooks';
import type { ICustomerNetAcquisitionLimit } from '@talos/kyoko';
import {
  ACTION,
  ButtonVariants,
  EMPTY_ARRAY,
  EntityAdmin,
  IconName,
  useCurrenciesContext,
  useDisclosure,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { ConfigureLimitsDialog } from './ConfigureLimitsDialog';
import {
  type AcquisitionLimitDrawerEntity,
  getAcquisitionLimitAsDrawerEntity,
  getAcquisitionLimitColumns,
  getAcquisitionLimitDrawerOptions,
  getAcquisitionLimitPostEntity,
} from './utils';

export function CustomerAcquisitionLimits() {
  const { currenciesList, currenciesBySymbol } = useCurrenciesContext();
  const { isAuthorized } = useRoleAuth();

  const allowEdit = isAuthorized(ACTION.EDIT_CUSTOMER_ACQUISITION_LIMITS);

  const columns = useMemo(() => {
    return getAcquisitionLimitColumns(currenciesBySymbol);
  }, [currenciesBySymbol]);
  const drawerOptions = useMemo(() => {
    return getAcquisitionLimitDrawerOptions(currenciesList);
  }, [currenciesList]);

  const configDialog = useDisclosure();
  const configDialogAction = useMemo(
    () => ({
      label: 'Global Configuration',
      onClick: () => configDialog.open(),
      variant: ButtonVariants.Priority,
      startIcon: IconName.Cog,
    }),
    [configDialog]
  );

  return (
    <>
      <ConfigureLimitsDialog {...configDialog} />
      <EntityAdmin<ICustomerNetAcquisitionLimit, AcquisitionLimitDrawerEntity>
        title="Customer Net Acquisition Limits"
        subtitle="View and manage customer net acquisition limits."
        entityName="Net Acquisition Limit"
        path="/customer-risk/net-acquisition/limits"
        entityIDField="LimitID"
        columns={columns}
        panelActions={[configDialogAction]}
        drawerOptions={drawerOptions}
        filterableProperties={EMPTY_ARRAY}
        allowModeSwitch={{
          isDisabled: () => !allowEdit,
        }}
        allowAddEntity={allowEdit}
        allowEditEntity={allowEdit}
        allowDeleteEntity={allowEdit}
        getEntityAsDrawerEntity={getAcquisitionLimitAsDrawerEntity}
        getEntityForPatchPost={getAcquisitionLimitPostEntity}
        persistKey="dealer-customer-net-acquisition-limits"
      />
    </>
  );
}
